import React from 'react';

import type CK from '~/types/contentking';

import DoubleArrowIcon from '~/components/app/DoubleArrowIcon';
import MenuItemLayout from '~/components/atoms/navigations/builders/MenuItemLayout';
import useAccountDisplayName from '~/hooks/useAccountDisplayName';
import useEffectiveHomeAccountId from '~/hooks/useEffectiveHomeAccountId';
import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useViewportType from '~/hooks/useViewportType';
import {
	isPhase3,
} from '~/utilities/viewportSize';

/**
 * Account Navigation Button Component
 *
 * Displays the current account name with double arrow icon.
 * Only visible when Technical section is expanded in new navigation.
 * Uses selectedAccountId if provided, otherwise falls back to effectiveHomeAccountId.
 */
type AccountNavButtonProps = {
	selectedAccountId?: CK.AccountId | null,
};

const AccountNavButton: React.FC<AccountNavButtonProps> = (props) => {
	const {
		selectedAccountId,
	} = props;
	const effectiveHomeAccountId = useEffectiveHomeAccountId();
	// Use selectedAccountId if provided, otherwise use effectiveHomeAccountId
	const accountIdToDisplay = selectedAccountId ?? effectiveHomeAccountId;
	const accountDisplayName = useAccountDisplayName(accountIdToDisplay);
	const viewportType = useViewportType();
	const [
		isMenuExpanded,
		,
		isMenuMinimized,
	] = useMenuExpandedState();

	// Check if we're in Phase 3 (either naturally or through menu expansion)
	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);

	// Show label when in Phase 3 (either naturally or through menu expansion)
	const shouldShowLabel = effectivePhase3;

	// Don't render if no account name available
	if (!accountDisplayName) {
		return null;
	}

	return (
		<MenuItemLayout
			icon={<DoubleArrowIcon size={20} />}
			isActive={false}
			isDimmed={false}
			isLabelHidden={!shouldShowLabel}
			label={accountDisplayName}
			labelModifier="account-nav"
		/>
	);
};

export default AccountNavButton;
